import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ChevronDown, BadgeCheck, MapPin, ArrowRight, Loader2, Building2, Compass, Users, Handshake, Star } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { STATE_ABBR } from "@/lib/stateMeta";

const STATES = Object.keys(STATE_ABBR).sort();
const TYPE_TABS = [
  { id: "", label: "All", icon: Handshake },
  { id: "brand", label: "Brands", icon: Building2 },
  { id: "outfitter", label: "Outfitters", icon: Compass },
  { id: "guide", label: "Guides", icon: Users },
];
const TYPE_LABEL = { brand: "Brand", outfitter: "Outfitter", guide: "Guide" };
const SORT_OPTIONS = [
  { id: "az", label: "A–Z" },
  { id: "newest", label: "Newest" },
  { id: "active", label: "Most Active" },
];
const VERIFY = {
  verified_business: "Verified", verified_retailer: "Verified", verified_outfitter: "Verified",
  verified_guide: "Verified", official_state: "Official",
};

export default function PartnerDirectory() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [type, setType] = useState(sp.get("type") || "");
  const [state, setState] = useState(sp.get("state") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [sort, setSort] = useState(sp.get("sort") || "az");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.partnerDirectory({ type: type || undefined, state: state || undefined, q: q || undefined, sort, limit: 60 })
      .then(setData).catch(() => setData({ partners: [], total: 0, counts: { all: 0 } })).finally(() => setLoading(false));
  }, [type, state, q, sort]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  // keep URL in sync
  useEffect(() => {
    const next = new URLSearchParams();
    if (type) next.set("type", type);
    if (state) next.set("state", state);
    if (q) next.set("q", q);
    if (sort && sort !== "az") next.set("sort", sort);
    setSp(next, { replace: true });
  }, [type, state, q, sort, setSp]);

  const counts = data?.counts || {};
  const partners = data?.partners || [];

  return (
    <div className="bg-paper min-h-screen" data-testid="partner-directory-page">
      <SiteHeader />

      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden" data-testid="directory-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-forest via-charcoal to-charcoal" />
        <div className="relative z-10 max-w-[1240px] mx-auto px-5 sm:px-10 py-14 lg:py-18">
          <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-blaze">HomeRange Partners</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-3">The partner directory.</h1>
          <p className="text-bone/75 mt-4 max-w-lg leading-relaxed">Discover verified brands, outfitters and guides trusted by hunters across the country. Search by type and state.</p>
        </div>
      </section>

      {/* Type tabs */}
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pt-6">
        <div className="flex flex-wrap gap-2" data-testid="directory-type-tabs">
          {TYPE_TABS.map((t) => {
            const n = t.id ? counts[t.id] : counts.all;
            return (
              <button key={t.id || "all"} onClick={() => setType(t.id)}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${type === t.id ? "bg-forest text-bone border-forest" : "bg-white text-charcoal/70 border-saddle/25 hover:border-forest"}`}
                data-testid={`dir-type-${t.id || "all"}`}>
                <t.icon size={15} /> {t.label} {typeof n === "number" && <span className={`text-xs ${type === t.id ? "text-bone/70" : "text-charcoal/40"}`}>({n})</span>}
              </button>
            );
          })}
        </div>

        {/* Search + state */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1 sm:max-w-xs">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partners…" className="w-full bg-white border border-saddle/25 rounded pl-10 pr-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="directory-search" />
          </div>
          <div className="relative">
            <select value={state} onChange={(e) => setState(e.target.value)} className="appearance-none bg-white border border-saddle/25 text-charcoal text-sm font-medium pl-3.5 pr-9 py-2.5 outline-none cursor-pointer hover:border-forest transition-colors rounded" data-testid="directory-state">
              <option value="">All states</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-charcoal/50 pointer-events-none" />
          </div>
          <div className="relative">
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort partners" className="appearance-none bg-white border border-saddle/25 text-charcoal text-sm font-medium pl-3.5 pr-9 py-2.5 outline-none cursor-pointer hover:border-forest transition-colors rounded" data-testid="directory-sort">
              {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{`Sort: ${o.label}`}</option>)}
            </select>
            <ChevronDown size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-charcoal/50 pointer-events-none" />
          </div>
          {(type || state || q) && (
            <button onClick={() => { setType(""); setState(""); setQ(""); }} className="text-sm font-semibold text-charcoal/50 hover:text-forest self-center" data-testid="directory-clear">Clear filters</button>
          )}
        </div>
      </div>

      <main className="max-w-[1240px] mx-auto px-5 sm:px-10 py-8 min-h-[40vh]">
        {loading ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : partners.length === 0 ? (
          <div className="py-20 text-center" data-testid="directory-empty">
            <Handshake size={30} className="mx-auto text-saddle/40" />
            <p className="font-serif text-2xl text-forest mt-4">No partners found</p>
            <p className="text-charcoal/55 mt-1">Try a different type or state.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-charcoal/55 mb-4" data-testid="directory-count">{data.total} partner{data.total === 1 ? "" : "s"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="directory-grid">
              {partners.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}>
                  <Link to={p.public_path} className="group block bg-white border border-saddle/20 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-saddle/40 transition-all duration-200" data-testid={`dir-card-${p.slug}`}>
                    <div className="relative h-32 bg-charcoal overflow-hidden">
                      {p.cover ? <img src={p.cover} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full bg-gradient-to-br from-forest to-charcoal" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-forest px-2 py-0.5 rounded-full">{TYPE_LABEL[p.partner_type]}</span>
                      {p.featured && <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-blaze text-white px-2 py-0.5 rounded-full" data-testid={`dir-featured-${p.slug}`}><Star size={9} className="fill-white" /> Featured</span>}
                    </div>
                    <div className="p-4 flex gap-3 -mt-8 relative">
                      <div className="w-14 h-14 rounded-xl bg-white border border-saddle/20 shadow overflow-hidden grid place-items-center shrink-0">
                        {p.logo ? <img src={p.logo} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-serif text-xl text-forest">{(p.name || "H")[0]}</span>}
                      </div>
                      <div className="min-w-0 pt-8">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-serif text-lg text-forest truncate">{p.name}</h3>
                          {VERIFY[p.verification_status] && <BadgeCheck size={15} className="text-blaze shrink-0" data-testid={`dir-verified-${p.slug}`} />}
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-4 -mt-1">
                      {p.tagline && <p className="text-sm text-charcoal/70 line-clamp-2">{p.tagline}</p>}
                      <div className="flex items-center gap-3 mt-2 text-xs text-charcoal/55 flex-wrap">
                        {p.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
                        {p.states_served?.length > 0 && <span>{p.states_served.slice(0, 4).join(" · ")}</span>}
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-blaze mt-3 group-hover:gap-2 transition-all">View profile <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Become a partner CTA */}
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-14">
        <div className="relative bg-forest text-bone rounded-xl overflow-hidden px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="directory-become-partner">
          <div className="flex-1">
            <h3 className="font-serif text-3xl leading-tight">Are you a brand, outfitter or guide?</h3>
            <p className="text-bone/70 mt-2 max-w-lg">List your business on HomeRange, get a verified profile, QR marketing tools and referral tracking.</p>
          </div>
          <button onClick={() => navigate("/partner/onboarding")} className="inline-flex items-center gap-2 bg-blaze text-white px-6 py-3.5 rounded-full font-semibold hover:bg-blaze-dark transition-colors shrink-0" data-testid="directory-cta-join">Become a Partner <ArrowRight size={16} /></button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
